
import axios from "axios";
import { apiUrl, getAuthHeader } from "./base";
// import AppUser from "../app-user/app-user";

async function analyzeStudentReportForStudent(
  studentId: string,
  submissionId: string,
  originalReport: string,
  deadlineDateStart: string,
  deadlineDateEnd: string,
  sectionDescription: { [key: string]: string },
  limitSectionWord: TPlainObject = {},
  formattedReportObject?: TPlainObject
) {
  try {
    const response = await axios.post(
      apiUrl("api/aria/report/student-analysis", "aria"),
      {
        studentId,
        submissionId,
        originalReport,
        deadlineDateStart,
        deadlineDateEnd,
        sectionDescription,
        limitSectionWord,
        sectionReport: formattedReportObject
      },
      await getAuthHeader("aria")
    );


    if (response.data.status !== "OK") {
      throw new Error(response.data.message ?? "Unable to process the request");
    }

    return {
      status: true,
      data: response.data.data,
      message: response.data.message,
    };
  } catch (err) {
    console.error("Error in analyzeStudentReportForStudent:", err);
    return {
      status: false,
      message: err?.response?.data?.message ?? err.message ?? "An unexpected error occurred",
    };
  }
}

async function analyzeStudentReportForTeacher(
  studentId: string,
  submissionId: string,
  sectionDescription: { [key: string]: string },
  formattedReportObject: TPlainObject = {}
) {
  try {
    const response = await axios.post(
      apiUrl("api/aria/report/teacher-analysis", "aria"),
      {
        studentId,
        submissionId,
        sectionDescription,
        sectionReport: formattedReportObject
      },
      await getAuthHeader('aria')
    );


    if (response.data.status !== "OK") {
      throw new Error(response.data.message ?? "Unable to process the request");
    }

    return {
      status: true,
      data: {
        ...response.data.data,
        report_too_short: !!response.data?.report_too_short
      },
      message: response.data.message,
    };
  } catch (err) {
    console.error("Error in analyzeStudentReportForTeacher:", err);
    return {
      status: false,
      message: err.message ?? "An unexpected error occurred",
    };
  }
}

async function regenerateAnalyzeStudentReportForTeacher(
  studentId: string,
  submissionId: string,
  sectionDescription: { [key: string]: string },
  formattedReportObject?: TPlainObject
) {
  try {
    const response = await axios.post(
      apiUrl("api/aria/report/regenerate-analysis", "aria"),
      {
        studentId,
        submissionId,
        sectionDescription,
        sectionReport: formattedReportObject
      },
      await getAuthHeader("aria")
    );


    if (response.data.status !== "OK") {
      throw new Error(response.data.message ?? "Unable to process the request");
    }

    return {
      status: true,
      data: {
        ...response.data.data,
        report_too_short: !!response.data?.report_too_short
      },
      message: response.data.message,
    };
  } catch (err) {
    console.error("Error in regenerateAnalyzeStudentReportForTeacher:", err);
    return {
      status: false,
      message: err.message ?? "An unexpected error occurred",
    };
  }
}

async function getStudentReportFeedback(
  userId: string,
  submissionId: string,
  additionalPrompt: string | null | undefined,
  highlightKeywords: string[]
) {
  try {
    // const user = await AppUser.getInfo();
    const response = await axios.post(
      apiUrl("api/aria/report/feedback", "aria"),
      {
        // userId: user?.id,
        userId,
        submissionId,
        additionalPrompt,
        highlightKeywords
      },
      await getAuthHeader("aria")
    );


    if (response.data.status !== "OK") {
      throw new Error(response.data.message ?? "Unable to process the request");
    }

    return {
      status: true,
      data: response.data.data,
      message: response.data.message,
    };
  } catch (err) {
    console.error("Error in getStudentReportFeedback:", err);
    return {
      status: false,
      message: err.message ?? "An unexpected error occurred",
    };
  }
}

async function regenerateStudentReportFeedback(
  userId: string,
  submissionId: string,
  additionalPrompt: string | null | undefined,
  highlightKeywords: string[],
) {
  try {
    // const user = await AppUser.getInfo();
    const response = await axios.post(
      apiUrl("api/aria/report/regenerate-feedback", "aria"),
      {
        // userId: user?.id,
        userId,
        submissionId,
        additionalPrompt,
        highlightKeywords,
      },
      await getAuthHeader("aria")
    );

    if (response.data.status !== "OK") {
      throw new Error(response.data.message ?? "Unable to process the request");
    }

    return {
      status: true,
      data: response.data.data,
      message: response.data.message,
    };

  } catch (err) {
    console.error("Error in regenerateStudentReportFeedback:", err);
    return {
      status: false,
      message: err.message ?? "An unexpected error occurred",
    };
  }
}

async function supervisorDashboardReportOverview(studentIds: string[]) {
  try {
    const response = await axios.post(
      apiUrl("api/aria/report/overview", "aria"),
      { studentIds },
      await getAuthHeader("aria")
    );

    if (response.data.status !== "OK") {
      throw new Error(response.data.message ?? "Unable to process the request");
    }

    return {
      status: true,
      data: response.data.data,
      message: response.data.message,
    }

  } catch (err) {
    console.error("Error in supervisorDashboardReportOverview:", err);
    return {
      status: false,
      message: err.message ?? "An unexpected error occurred",
    };
  }
}

async function getSubmissionAnalysisHistory(userId: string, submissionIds: string[]) {
  try {
    const submissionQuery = submissionIds.join(",");
    const response = await axios.get(
      apiUrl(`api/aria/report/history/${userId}?submissions=${submissionQuery}`, "aria"),
      await getAuthHeader("aria")
    );

    if (!response.data.status) {
      throw new Error(response.data.message ?? "Failed to retrieve analysis history");
    }

    return {
      status: true,
      data: response.data.data,
      message: response.data.message,
    };
  } catch (err: any) {
    console.error("Error in getSubmissionAnalysisHistory:", err);

    return {
      status: false,
      message: err.message ?? "An unexpected error occurred",
    };
  }
}

const ApiARIAAnalyze = {
  analyzeStudentReportForStudent,
  analyzeStudentReportForTeacher,
  regenerateAnalyzeStudentReportForTeacher,
  getStudentReportFeedback,
  regenerateStudentReportFeedback,
  supervisorDashboardReportOverview,
  getSubmissionAnalysisHistory,
};

export default ApiARIAAnalyze;
